import { ChartControls } from "@/components/charts/ChartControls";
import { SeriesToggleLegend } from "@/components/charts/SeriesToggleLegend";
import { TotalMetricCard } from "@/components/charts/TotalMetricCard";
import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { useChartQuery } from "@/hooks/useChartQuery";
import { grainAtom, metricAtom } from "@/state/chart.state";
import { localeAtom } from "@/state/locale.state";
import { unitSystemAtom } from "@/state/units.state";
import type { ChartGrain } from "@/types/chart.types";
import { SPORT_COLORS, sportLabel } from "@/types/sports.meta";
import {
  formatDistance,
  formatElevation,
  formatTime,
} from "@/utils/format.utils.ts";
import { isSportType } from "@/utils/sports.utils";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useAtom, useAtomValue } from "jotai";
import * as React from "react";
import { Bar, BarChart, LabelList, XAxis, YAxis } from "recharts";

function formatPeriod(value: string, grain: ChartGrain, locale: string) {
  const fmt = (date: Date, opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, opts).format(date);

  // Normalize
  if (grain === "months" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    value = value.slice(0, 7); // YYYY-MM
  }

  if (grain === "months") {
    // value: YYYY-MM
    const m = value.match(/^(\d{4})-(\d{2})$/);
    if (!m) return value;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = new Date(Date.UTC(y, mo - 1, 1));
    return fmt(d, { month: "short", year: "2-digit", timeZone: "UTC" });
  }

  // days/weeks: value: YYYY-MM-DD
  const dm = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dm) return value;

  const y = Number(dm[1]);
  const mo = Number(dm[2]);
  const day = Number(dm[3]);
  const d = new Date(Date.UTC(y, mo - 1, day));
  return fmt(d, { month: "short", day: "numeric", timeZone: "UTC" });
}

export function ChartsPage() {
  const [metric, setMetric] = useAtom(metricAtom);
  const [grain, setGrain] = useAtom(grainAtom);

  const { i18n } = useLingui();
  const locale = useAtomValue(localeAtom);
  const unitSystem = useAtomValue(unitSystemAtom);

  const { data, isError } = useChartQuery();

  const chartData = data?.chartData ?? [];
  const series = data?.series ?? [];
  const totalValue = data?.totalValue;

  // Local legend toggles
  const [hidden, setHidden] = React.useState<Record<string, boolean>>({});

  const toggleSeries = React.useCallback((key: string) => {
    setHidden((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const visibleSeries = React.useMemo(
    () => series.filter((s) => !hidden[s]),
    [series, hidden],
  );

  const chartDataWithTotal = React.useMemo(() => {
    return chartData.map((row) => {
      const total = visibleSeries.reduce(
        (sum, s) => sum + Number(row[s] ?? 0),
        0,
      );
      return { ...row, __total: total };
    });
  }, [chartData, visibleSeries]);

  const chartConfig: ChartConfig = Object.fromEntries(
    series.map((s) => {
      const label = isSportType(s) ? i18n._(sportLabel(s)) : s;
      const color = isSportType(s) ? SPORT_COLORS[s] : "var(--chart-1)";
      return [
        s,
        {
          label,
          color,
        },
      ];
    }),
  );

  function formatValue(v: number) {
    const n = Number(v) || 0;
    if (metric === "distance") return formatDistance(n, locale, unitSystem);
    if (metric === "total_elevation_gain")
      return formatElevation(n, locale, unitSystem);
    if (metric === "elapsed_time" || metric === "moving_time")
      return formatTime(n, locale);
    return `${v}`;
  }

  const metricLabel = React.useMemo(() => {
    switch (metric) {
      case "distance":
        return t`Distance`;
      case "moving_time":
        return t`Moving time`;
      case "elapsed_time":
        return t`Total time`;
      case "total_elevation_gain":
        return t`Elevation gain`;
      default:
        return metric;
    }
  }, [metric]);

  const lastVisibleKey = React.useMemo(() => {
    for (let i = series.length - 1; i >= 0; i--) {
      const key = series[i];
      if (!hidden[key]) return key;
    }
    return null;
  }, [series, hidden]);

  if (isError) {
    return (
      <div className="p-4 text-sm text-destructive">
        <Trans>Failed to load chart.</Trans>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-3 md:p-4 min-w-0 justify-center">
      <ChartControls
        metric={metric}
        onMetricChange={setMetric}
        grain={grain}
        onGrainChange={setGrain}
      />

      {grain === "total" ? (
        <TotalMetricCard
          metricLabel={metricLabel}
          value={formatValue(Number(totalValue ?? 0))}
        />
      ) : (
        <ChartContainer
          config={chartConfig}
          className="h-[clamp(160px,40vh,360px)] w-full"
        >
          <BarChart
            accessibilityLayer
            data={chartDataWithTotal}
            margin={{ top: 24, right: 8, left: 0, bottom: 0 }}
          >
            <XAxis
              dataKey="period"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              tickFormatter={(v) => formatPeriod(String(v), grain, locale)}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={formatValue}
            />

            {series.map((s) => (
              <Bar
                key={s}
                dataKey={s}
                stackId="a"
                fill={`var(--color-${s})`}
                isAnimationActive={false}
                hide={hidden[s] === true}
              >
                {lastVisibleKey === s ? (
                  <LabelList
                    dataKey="__total"
                    position="top"
                    formatter={formatValue}
                    className="text-[11px] fill-muted-foreground"
                  />
                ) : null}
              </Bar>
            ))}

            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelKey="period"
                  labelFormatter={(_, d) => {
                    const period = d?.[0]?.payload?.period;
                    return formatPeriod(String(period), grain, locale);
                  }}
                  valueFormatter={(value) => formatValue(Number(value))}
                />
              }
            />

            <ChartLegend
              content={() => (
                <SeriesToggleLegend
                  series={series}
                  hidden={hidden}
                  onToggle={toggleSeries}
                />
              )}
            />
          </BarChart>
        </ChartContainer>
      )}
    </div>
  );
}
